import type { Film, FilmType } from '@/types/film';
import { films } from '@/mocks/films';

export const GENRES = ['动作', '爱情', '科幻', '悬疑', '剧情', '喜剧', '犯罪', '奇幻', '历史', '动画', '冒险'];

export const REGIONS = ['中国', '美国', '韩国', '日本', '英国', '德国'];

export const YEARS = [2026, 2025, 2024, 2023, 2022];

export const SORT_OPTIONS = [
  { value: 'latest', label: '最新上线' },
  { value: 'hot', label: '热度最高' },
  { value: 'rating', label: '评分最高' },
] as const;

export const PAGE_SIZES = [8, 12, 20];

export type SortKey = (typeof SORT_OPTIONS)[number]['value'];

export interface FilterState {
  type?: FilmType;
  genre?: string;
  region?: string;
  year?: number;
  keyword?: string;
}

export const typeLabel = (type: FilmType) => (type === 'movie' ? '电影' : '电视剧');

export const formatHeat = (heat: number) => {
  if (heat >= 10000) return `${(heat / 10000).toFixed(1)} 万人在看`;
  return `${heat} 人在看`;
};

export const getFilmById = (id?: string) => films.find((film) => film.id === id);

export const filterFilms = (source: Film[], state: FilterState) => {
  const keyword = state.keyword?.trim().toLowerCase();
  return source.filter((film) => {
    if (state.type && film.type !== state.type) return false;
    if (state.genre && !film.genres.includes(state.genre)) return false;
    if (state.region && film.region !== state.region) return false;
    if (state.year && film.year !== state.year) return false;
    if (keyword) {
      const haystack = [film.title, film.enTitle, film.director, film.region, ...film.genres, ...film.cast, ...film.tags]
        .join(' ')
        .toLowerCase();
      if (!haystack.includes(keyword)) return false;
    }
    return true;
  });
};

export const sortFilms = (list: Film[], sort: SortKey = 'latest') => {
  const copy = [...list];
  if (sort === 'hot') return copy.sort((a, b) => b.heat - a.heat);
  if (sort === 'rating') return copy.sort((a, b) => b.rating - a.rating);
  return copy.sort((a, b) => b.year - a.year || b.heat - a.heat);
};

export const paginate = <T,>(list: T[], page: number, size: number) => {
  const totalPages = Math.max(1, Math.ceil(list.length / size));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const start = (safePage - 1) * size;
  return {
    items: list.slice(start, start + size),
    totalPages,
    page: safePage,
    total: list.length,
  };
};

export const getRelatedFilms = (film: Film, count = 4) => {
  const scored = films
    .filter((item) => item.id !== film.id)
    .map((item) => {
      const sharedGenres = item.genres.filter((genre) => film.genres.includes(genre)).length;
      const sameDirector = item.director === film.director ? 2 : 0;
      return { item, score: sharedGenres * 2 + sameDirector };
    })
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score || b.item.heat - a.item.heat);
  return scored.slice(0, count).map((entry) => entry.item);
};

export const hotFilms = (count = 6) => [...films].sort((a, b) => b.heat - a.heat).slice(0, count);

export const latestFilms = (count = 8) =>
  [...films].sort((a, b) => b.year - a.year || b.heat - a.heat).slice(0, count);

export const countByType = (type: FilmType) => films.filter((film) => film.type === type).length;